import { ShowableProductCardNs } from "ReactApp/Types/ShowableProductCardNs";
import { DomainNs } from "ReactApp/Types/DomainNs";
import { 
    ShowableProductCardHtmlIdentifierEnum,
    ProductTypeEnum,
    VirtualModifierSwitcherTypeEnum,
    PriceTypeEnum,
    ProductSaleStatusTypeEnum,
} from "ReactApp/Types/enum";
import {
    makeObservable, observable,
    computed, action, runInAction
} from "mobx";
import { ProductInfoNs } from "ReactApp/Types/ProductInfoNs";
import { CartNs } from "ReactApp/Types/CartNs";
import { cartInstance } from "./Cart";
import { DeliveryNs } from "ReactApp/Types/DeliveryNs";
import { deliveryRawSetToDeliverySet, generateDeliveryObjectKey } from "ReactApp/Services/Delivery/Common";
import { SpecialBadgeNs } from "ReactApp/Types/SpecialBadgeNs";

/**
 * On the server, it is transformed from the ProductCardAggregateV1 aggregate
 * @see app/common/ddd/aggregates/common/widget/ProductCardAggregateV1
 */
export class ShowableProductCard 
    implements ShowableProductCardNs.ShowableProductCardInterface
{
    // TODO[needImpl]: see task docs/plans-by-release/0_9_2.md
    // searchBy: 2023-06-29:2
    resourceUrl: string = '/product-view-configurator';
    resourceAdditionalProductUrl: string = '/product-view-additional-product';
    isCartDataLoaded: boolean = false;
    isHtmlServerAppStatus = false;
    // TODO[needImpl]: see task docs/plans-by-release/0_9_2.md
    // searchBy: 2023-06-29:2

    // TODO[needImpl] нужно реализовать забор валюты. пока хардкод
    currency: string | null = null;

    private static _instance: ShowableProductCard;

    type: ProductTypeEnum | null = null;
    saleStatusType: ProductSaleStatusTypeEnum | null = null;
    baseTitle: string | null = null;
    title: string | null = null;
    marketingProductId: number | null = null;
    baseCardId: number | null = null;
    realModifierId: number | null = null;
    virtualModifierId: number | null = null;
    isManuallySetModifier: boolean | null = null;
    url: string | null = null;
    salePrice: DomainNs.PriceFieldInterface | null = null;
    oldPrice: DomainNs.PriceFieldInterface | null = null;
    baseCardFields: DomainNs.CharacterFieldInterface[] = [];
    actualFields: DomainNs.CharacterFieldInterface[] = [];
    realModifiers: DomainNs.RealModifierInterface[] = [];
    virtualModifiers: DomainNs.VirtualModifierInterface[] = [];
    currentProductConfig: CartNs.CurrentProductConfigInterface | null = null;
    deliverySet: DeliveryNs.DeliverySet = new Map();
    currentDeliveryObject: DeliveryNs.CurrentDeliveryObjectInterface | null = null;
    badges: SpecialBadgeNs.SpecialBadge[] = [];

    favoriteInfo: ProductInfoNs.WithFavoriteInfoInterface = {
        status: null
    }
    cartInfo: ProductInfoNs.WithCartInfoInterface = {
        number: null,
        isDeleted: false,
        isDiscounted: false,
        isStaticDiscount: null,
        discountValue: null,
        promoPrice: null
    }

    //new extended fields //mark for me for future mobx state managment
    stock: DomainNs.StockInterface | null = null;
    articul: DomainNs.ArticulInterface | null = null;

    //redo
    rating: DomainNs.RatingInterface | null = null;
    countReviews: number | null = null;
    countOrders: number | null = null;

    additionalProducts: CartNs.AdditionalProductInterface[] = [];

    lastRealModifierData: {
        salePrice: DomainNs.PriceFieldInterface | null,
        oldPrice: DomainNs.PriceFieldInterface | null,
        actualFields: DomainNs.CharacterFieldInterface[]
    } = {
        salePrice: null,
        oldPrice: null,
        actualFields: []
    };

    category: DomainNs.CategoryInfoInterface | null = null;
    brand: DomainNs.BrandInfoInterface | null = null;

    // cache for additional product requests
    private _additionalProductCache: Map<string, any> = new Map();

    // индикатор загрузки с сервер для доп продуктов
    isLoadingAdditionalProducts: boolean = false;


    public static get Instance() {
        return this._instance || (this._instance = new this());
    }

    private constructor() {

        this.initialLoadFromHtmlServerApp();

        makeObservable(this, {
            type: observable,
            salePrice: observable,
            oldPrice: observable,
            baseCardFields: observable,
            realModifiers: observable,
            virtualModifiers: observable,
            actualFields: observable,
            favoriteInfo: observable,
            cartInfo: observable,
            currentProductConfig: observable,
            currentDeliveryObject: observable,
            isLoadingAdditionalProducts: observable,
            // calculated/action/flow/etc
            discountPercentage: computed,
            textileSwitchers: computed,
            recomputedDeliveryObject: action,
            recomputedCurrentProductConfig: action,
            recomputedVirtualModifierData: action,
            recomputedRealModifierData: action,
            resetToLastRealModifierData: action,
            switchVirtualModifier: action,
            switchRealModifier: action,
            updateAllVirtualModifiers: action,
            setNewRealModifierData: action,
            toCart: action,
        });

    }

    getCurrentId(): number {
        if (this.virtualModifierId !== null) {
            return this.virtualModifierId;
        } else if (this.realModifierId !== null) {
            return this.realModifierId;
        } else if (this.baseCardId !== null) {
            return this.baseCardId;
        }
        return -1;
    }

    getCurrentProductType(): ProductTypeEnum
    {
        if (this.virtualModifierId !== null) {
            return ProductTypeEnum.virtualModifier;
        } else if (this.realModifierId !== null) {
            return ProductTypeEnum.realModifier;
        }
        return ProductTypeEnum.card;
    }

    toCart() {
        if (this.currentProductConfig !== null) {
            this.currentProductConfig.isInCart = true;
        }
    }

    recomputedDeliveryObject()
    {
        let oInfo: DeliveryNs.DeliveryObjectInfoInterface = {
            type: this.getCurrentProductType(),
            id: this.getCurrentId(),
        };
        let o: DeliveryNs.CurrentDeliveryObjectInterface = {
            info: oInfo, 
            value: this.deliverySet.get(generateDeliveryObjectKey(
                oInfo.type, oInfo.id
            )),
        };
        this.currentDeliveryObject = o;
    }

    recomputedCurrentProductConfig()
    {
        let config: CartNs.CurrentProductConfigInterface = {
            productTypeId: (this.type as ProductTypeEnum),
            id: (this.type === ProductTypeEnum.realModifier) 
                ? (this.realModifierId as number)
                : (this.baseCardId as number),
            virtualModifierId: this.virtualModifierId,
            // for further adding
            count: 1,
            isInCart: false,
            getUniqueId: function()  {
                return `${this.id}-${this.productTypeId}-${this.virtualModifierId}`
            }
        };
        const cartPosition = cartInstance.productSet?.find(
            config.productTypeId, config.id,
            (config.virtualModifierId !== null) 
                ? config.virtualModifierId
                : undefined
        ) || null;
        if (cartPosition !== null) {
            config.count = cartPosition.count ?? 0;
            config.isInCart = true;
        }
        this.currentProductConfig = config;
    }

    get textileSwitchers(): DomainNs.VirtualModifierInterface[]
    {
        let textiles: DomainNs.VirtualModifierInterface[] = [];
        textiles = this.virtualModifiers.filter(v => {
            return (
                v.switcherData.virtualModifierSwitcherType
                    ===
                VirtualModifierSwitcherTypeEnum.textile
            );
        });
        return textiles;
    }

    get discountPercentage(): number | null
    {
        if (
            this.oldPrice !== null
                &&
            this.salePrice !== null
                &&
            this.oldPrice.isFill === true
                &&
            this.salePrice.isFill === true
                &&
            this.oldPrice.value !== null 
                && 
            this.salePrice.value !== null 
        ) {
            return Math.round(
                (
                    (this.oldPrice.value - this.salePrice.value)
                        / 
                    this.oldPrice.value
                ) * 100
            );
        }
        return null;
    }

    switchVirtualModifier(virtualModifierId: number, status: boolean) {
        let that = this;
        // reset all VM's
        that.virtualModifiers.map(v => v.isActive = false);
        if (status === false) {
            // set currently
            that.virtualModifiers.map(v => {
                if (v.switcherData.virtualModifierId === virtualModifierId) {
                    v.isActive = !v.isActive;
                    // TODO[needImpl]: do not forget when designing
                    that.virtualModifierId = v.switcherData.virtualModifierId;
                    // \TODO[needImpl]: do not forget when designing
                }
            });
        } else {
            // TODO[needImpl]: do not forget when designing
            that.virtualModifierId = null;
            // \TODO[needImpl]: do not forget when designing
        }
        // recomputed card
        that.recomputedVirtualModifierData();
        that.recomputedCurrentProductConfig();
        that.recomputedDeliveryObject();
    }

    switchRealModifier(realModifierId: number) {

        let that = this;
        // reset all RM's
        that.realModifiers.map(v => {
            if (v.realModifierId !== realModifierId) {
                v.isActive = false;
            }
        });
        // set currently
        that.realModifiers.map(v => {
            if (v.realModifierId === realModifierId) {
                v.isActive = true;
            }
            // TODO[needImpl]: do not forget when designing
            that.realModifierId = realModifierId;
            that.virtualModifierId = null;
            // \TODO[needImpl]: do not forget when designing
        });
        // recomputed card
        that.recomputedRealModifierData();
        that.recomputedCurrentProductConfig();
        that.recomputedDeliveryObject();

        that.recomputedAdditionalProductList();

        // меняются некоторые ручные данные
        that.isManuallySetModifier = true; 
    }
    
    /**
     * Подготовка конфиги для модального окна 
     * покупки в 1 клик
     * @returns []
     */
    get buyInClickModalConfig(): CartNs.BuyInClickModalConfigInterface|[] {
        if (
            !this.salePrice?.value 
                || 
            !this.currentProductConfig?.count
                ||
            !this.title
        ) {
            return [];
        }
        return {
            positionTypeId: this.getCurrentProductType(),
            positionId: this.getCurrentId(),
            productId: this.baseCardId as number,
            price: this.salePrice?.value,
            count: this.currentProductConfig?.count,
            sum: (this.salePrice?.value * this.currentProductConfig?.count),
            nameAtTimeOrder: this.title
        };
    }

    /**
     * Подготовка конфиги для модального окна 
     * покупки в 1 клик
     * @returns []
     */
    get foundCheaperModalConfig(): CartNs.FoundCheaperModalConfigInterface|[] {
        if (!this.salePrice?.value) {
            return [];
        }
        return {
            positionTypeId: this.getCurrentProductType(),
            positionId: this.getCurrentId(),
            productId: this.baseCardId as number,
            ourPrice: this.salePrice?.value,
        };
    }

    //Пересчитываем дополнительные продукты
    //делаем запрос на сервер или возращаем и кэша
    public async recomputedAdditionalProductList() {
        let that = this;
        if (!that.currentProductConfig) {
            this.isLoadingAdditionalProducts = false;
            return;
        }

        const cacheKey = that.currentProductConfig.getUniqueId();
        if (this._additionalProductCache.has(cacheKey)) {
            this.additionalProducts = this._additionalProductCache.get(cacheKey);
            return;
        }
        runInAction(() => {
            this.isLoadingAdditionalProducts = true;
        });
        //сразу создаю пустой элемент чтобы не делать повторные запросы пока await не закончится
        this._additionalProductCache.set(cacheKey, null);
        const data = await this.fetchAdditionalProductList();

        runInAction(() => {
            this._additionalProductCache.set(cacheKey, data);
            this.additionalProducts = data;
            this.isLoadingAdditionalProducts = false;
        });
    }

    public async fetchAdditionalProductList() {
        const response = await fetch(
            this.buildRequestUrl(this.resourceAdditionalProductUrl)
        );
        if (!response.ok)
            throw new Error('Failed to fetch additional products');

        return response.json();
    }

    resetToLastRealModifierData() {
        let that = this;
        that.salePrice = that.lastRealModifierData.salePrice;
        that.oldPrice = that.lastRealModifierData.oldPrice;
        that.actualFields = that.lastRealModifierData.actualFields;
    }

    setNewRealModifierData() {
        let that = this;
        that.lastRealModifierData.salePrice = that.salePrice;
        that.lastRealModifierData.oldPrice = that.oldPrice;
        that.lastRealModifierData.actualFields = that.actualFields;
    }

    recomputedVirtualModifierData() {
        let that = this;
        // let current VM
        let currentVirtualModifier = that.virtualModifiers.filter(v => {
            return v.isActive === true;
        }).shift();
        if (currentVirtualModifier === undefined) {
            // to last real modifier data
            that.resetToLastRealModifierData();
            return;
        }

        let isSalePriceSetted, isOldPriceSetted;
        isSalePriceSetted = isOldPriceSetted = false;

        // first: modified data
        currentVirtualModifier.modifiedData.forEach(md => {
            if (that.isPriceField(md)) {
                if (md.priceType === PriceTypeEnum.sale) {
                    that.salePrice = md;
                    isSalePriceSetted = true;
                }
                if (md.priceType === PriceTypeEnum.old) {
                    that.oldPrice = md;
                    isOldPriceSetted = true;
                }
            }
            if (that.isCharacterField(md)) {
                that.actualFields.forEach((af, i) => {
                    if (md.commonKey === af.key) {
                        that.actualFields[i].value = md.value;
                    }
                });
            }
        });
        if (isSalePriceSetted === false) {
            that.salePrice = null;
        }
        if (isOldPriceSetted === false) {
            that.oldPrice = null;
        }


        // second: signs
        currentVirtualModifier.signs.forEach(s => {
            that.actualFields.forEach((af, i) => {
                if (s.commonKey === af.key) {
                    that.actualFields[i].value = s.value;
                }
            });
        });
    }

    recomputedRealModifierData() {
        let that = this;
        // let current RM
        let currentRealModifier = that.realModifiers.filter(v => {
            return v.isActive === true;
        }).shift();
        if (currentRealModifier === undefined) {
            // error
            return;
        }

        // update all VM's
        that.updateAllVirtualModifiers(currentRealModifier);

        let isSalePriceSetted, isOldPriceSetted;
        isSalePriceSetted = isOldPriceSetted = false;

        // change all data
        // first: modified data
        currentRealModifier.modifiedData.forEach(md => {
            if (that.isPriceField(md)) {
                if (md.priceType === PriceTypeEnum.sale) {
                    that.salePrice = md;
                    isSalePriceSetted = true;
                }
                if (md.priceType === PriceTypeEnum.old) {
                    that.oldPrice = md;
                    isOldPriceSetted = true;
                }
            }
            if (that.isCharacterField(md)) {
                that.actualFields.forEach((af, i) => {
                    if (md.commonKey === af.key) {
                        that.actualFields[i].value = md.value;
                    }
                });
            }
        });
        if (isSalePriceSetted === false) {
            that.salePrice = null;
        }
        if (isOldPriceSetted === false) {
            that.oldPrice = null;
        }
        
        // second: signs
        currentRealModifier.signs.forEach(s => {
            that.actualFields.forEach((af, i) => {
                if (s.commonKey === af.key) {
                    that.actualFields[i].value = s.value;
                }
            });
        });

        // set new last realModifierData
        that.setNewRealModifierData();
        
        // change URL.history for new URL
        window.history.pushState({}, '', currentRealModifier.url);
    }

    updateAllVirtualModifiers(realModifier: DomainNs.RealModifierInterface) {
        let that = this;
        // reset all data to first rm
        that.resetToLastRealModifierData();
        // unactive and unavailable all VM's
        that.virtualModifiers.forEach(v => {
            v.isActive = false; 
            v.isAvailable = false;
        });
        // available all VM's for current RM
        that.virtualModifiers.forEach(v => {
            if (v.realModifierId === realModifier.realModifierId) {
                v.isAvailable = true;
            }
        });
    }

    isPriceField = (f: DomainNs.ProductFieldInterface): f is DomainNs.PriceFieldInterface => {
        return 'priceType' in f;
    }

    isCharacterField = (f: DomainNs.ProductFieldInterface): f is DomainNs.CharacterFieldInterface => {
        return !('priceType' in f);
    }
    
    // TODO[needImpl]: see task docs/plans-by-release/0_9_2.md
    // searchBy: 2023-06-29:2
    async initialize(): Promise<boolean> {

        if (this.isHtmlServerAppStatus === false) {
            return new Promise(resolve => resolve(this.isCartDataLoaded));
        }

        let that = this;

        let retries: number = 5;
        let maxRetries: number = retries - 1;
        let retryTimeout: number = 3000;

        while (retries-- > 0 && this.isCartDataLoaded === false) {
            if (retries === maxRetries) {
                await new Promise(resolve => {
                    document.addEventListener("DOMContentLoaded", async (event) => {
                        await that.fetch()
                            .then(stat => this.isCartDataLoaded = stat)
                            .catch(err => {
                                console.log(err);
                                console.error(
                                    'The first attempt loading of the product configurator is fail! Error: ' +
                                    err.message 
                                );
                            });
                        resolve(null);
                    });
                });
            } else {
                await new Promise(resolve => {
                    setTimeout(async () => {
                        await that.fetch()
                            .then(stat => this.isCartDataLoaded = stat)
                            .catch(err => {
                                console.log(err);
                                console.error(
                                    `The another [${retries + 1}] attempt loading ` + 
                                    `of the product configurator is fail! Error: ` +
                                    err.message 
                                );
                            });
                        resolve(null);
                    }, retryTimeout);
                });
            }
        }

        if (this.isCartDataLoaded === true) {
            this.recomputedCurrentProductConfig();
            this.recomputedDeliveryObject();
            // сохраняю текущие доп продукты в кэш чтобы не делать лишние запросы
            if (this.currentProductConfig) {
                const cacheKey = this.currentProductConfig.getUniqueId();
                this._additionalProductCache.set(cacheKey, this.additionalProducts);
            }

        }

        return new Promise(resolve => resolve(this.isCartDataLoaded));
    }

    async fetch(): Promise<boolean> {

        let that = this;
        let success: boolean = false;
        let error: Error;
        let response: Response;

        response = await fetch(that.buildRequestUrl(), {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            },
        }).catch(err => error = err);

        if (response.ok === true) {
            // set data
            success = true;
            let json = await response.json();

            json.actualFields = json.baseCardFields;
            json.lastRealModifierData = {
                salePrice: json.salePrice,
                oldPrice: json.oldPrice,
                actualFields: json.actualFields,
            };

            json.deliverySet = deliveryRawSetToDeliverySet(
                json.deliveryRawSet
            );

            Object.assign(that, json);
        }
        
        return new Promise((resolve, reject) => {
            if (error) {
                return reject(new Error(error.message));
            }
            if (response.ok === false) {
                return reject(new Error(response.statusText));
            }
            resolve(success);
        });
    }

    initialLoadFromHtmlServerApp(): void {

        // first load data from the exists html from PHP-based server-app
        let productCardWrapHtmlElement =
            document.getElementById(ShowableProductCardHtmlIdentifierEnum.wrapId);
        if (
            typeof(productCardWrapHtmlElement) === undefined 
                || 
            productCardWrapHtmlElement === null
        ) {
            // throw new InvalidInitialLoadFromServerAppError(
            //     `ShowableProductCard not load from the exist ` + 
            //     `HTML generated by the server-app, because the HTML-wrap element ` +
            //     `with a ID [${ShowableProductCardHtmlIdentifierEnum.wrapId}] not found!`
            // );
            console.warn(
                `ShowableProductCard not load from the exist ` + 
                `HTML generated by the server-app, because the HTML-wrap element ` +
                `with a ID [${ShowableProductCardHtmlIdentifierEnum.wrapId}] not found!`
            );
            this.isHtmlServerAppStatus = false;
            return;
        }

        let productTypeId: number = parseInt(productCardWrapHtmlElement.getAttribute(
            ShowableProductCardHtmlIdentifierEnum.wrapProductTypeIdAttributeName
        ) || '');
        let baseCardId: number = parseInt(productCardWrapHtmlElement.getAttribute(
            ShowableProductCardHtmlIdentifierEnum.wrapBaseCardIdAttributeName
        ) || '');
        let realModifierId: number = parseInt(productCardWrapHtmlElement.getAttribute(
            ShowableProductCardHtmlIdentifierEnum.wrapRealModifierIdAttributeName
        ) || '');
        let isManuallySetModifier: number = parseInt(productCardWrapHtmlElement.getAttribute(
            ShowableProductCardHtmlIdentifierEnum.wrapIsManuallySetModifierAttributeName
        ) || '');

        if (
            (productTypeId === null || baseCardId === null)
                ||
            !(productTypeId in ProductTypeEnum)
                ||
            // or it's RealModifier and realModifierId is not set
            (
                (ProductTypeEnum.realModifier === productTypeId)
                    &&
                isNaN(realModifierId)
            )
        ) {
            // throw new InvalidInitialLoadFromServerAppError(
            //     `ShowableProductCard not load from the exist ` + 
            //     `HTML generated by the server-app, because the initial-data is not valid!`
            // );
            console.warn(
                `ShowableProductCard not load from the exist ` + 
                `HTML generated by the server-app, because the initial-data is not valid!`
            );
            this.isHtmlServerAppStatus = false;
            return;
        }

        this.type = productTypeId;
        this.baseCardId = baseCardId;
        this.isManuallySetModifier = (isManuallySetModifier == 1) ? true : false;

        this.realModifierId = realModifierId;
        this.isHtmlServerAppStatus = true;

        let rating: number = parseInt(productCardWrapHtmlElement.getAttribute(
            ShowableProductCardHtmlIdentifierEnum.ratingBlockWrapNumberStarsAttributeName
        ) || '');

        let countOrders: number = parseInt(productCardWrapHtmlElement.getAttribute(
            ShowableProductCardHtmlIdentifierEnum.countOrdersAttributeName
        ) || '');

         let countReviews: number = parseInt(productCardWrapHtmlElement.getAttribute(
            ShowableProductCardHtmlIdentifierEnum.countReviewsAttributeName
        ) || '');

        this.rating = rating != null ? {numberStars: rating} : null;
        this.countOrders = countOrders != null ? countOrders : null;
        this.countReviews = countReviews != null ? countReviews : null;
    }

    buildRequestUrl(resourceUrl = this.resourceUrl): string
    {
        if (this.type === ProductTypeEnum.realModifier) {
            return `${resourceUrl}?${new URLSearchParams({
                baseCardId: String(this.baseCardId),
                realModifierId: String(this.realModifierId),
                isManuallySetModifier: String(this.isManuallySetModifier ? 1 : 0),
            })}`;
        } else {
            return `${resourceUrl}?${new URLSearchParams({
                baseCardId: String(this.baseCardId),
            })}`
        }
    }
    // TODO[needImpl]: see task docs/plans-by-release/0_9_2.md
    // searchBy: 2023-06-29:2
    convertStringToProductTypeEnum(intValue: string): ProductTypeEnum | null {
        switch (intValue) {
            case "1":
                return ProductTypeEnum.card;
            case "2":
                return ProductTypeEnum.realModifier;
            case "3":
                return ProductTypeEnum.virtualModifier;
            default:
                return null;
        }
    }
}

export const showableProductCardInstance = ShowableProductCard.Instance;